import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';
import {EdgeLabelRenderer, EdgeProps, getBezierPath} from 'reactflow';

import {PipelineStepStatusCode} from 'graphql-client/__generated/graphqlGlobalTypes';
import {getPipelineStepRawDataKind} from 'pipelines/StepDataUtils';
import {getIconForStepDataKind} from 'pipelines/StepIconUtils';
import * as Colors from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import BaseEdgeRenderer from 'ui-library/reactflow/edges/BaseEdgeRenderer';
import {EdgeType, LineStyle} from 'ui-library/reactflow/edges/edgeOptions';
import {MakeFieldsRequired} from 'util/Types';

import {AnalysisGraphEdgeData} from './analysis-graph/AnalysisGraphTypes';
import AnalysisGraphUtils from './analysis-graph/AnalysisGraphUtils';
import {
  ANALYSIS_FLOWCHART_BACKGROUND,
  ANALYSIS_FLOWCHART_EDGE_LABEL_ICON_HEIGHT,
} from './AnalysisFlowchartStyleConstants';
import RawDataPopover from './popovers/RawDataPopover';

export default function AnalysisFlowchartEdge(
  props: MakeFieldsRequired<EdgeProps<AnalysisGraphEdgeData>, 'data'>
): JSX.Element {
  const [, labelX, labelY] = getBezierPath(props);

  const rawDatas = AnalysisGraphUtils.getEdgeRawDatas(props.data.edge);

  // Style edge as dashed/purple if either connected step is AI_PROPOSED (pending user approval)
  const isAiProposed =
    props.data.edge.fromStep.statusCode === PipelineStepStatusCode.AI_PROPOSED ||
    props.data.edge.toStep.statusCode === PipelineStepStatusCode.AI_PROPOSED;

  // We currently only show an edge label if there is a single data frame; in the future we should handle
  // charts, files, and multiple data frames.
  const {data: _originalData, ...restProps} = props;
  return (
    <>
      <BaseEdgeRenderer
        {...restProps}
        data={{
          type: EdgeType.BEZIER,
          lineStyle: isAiProposed ? LineStyle.DASHED : LineStyle.SOLID,
          color: isAiProposed ? Colors.PURPLE_DARK : undefined,
        }}
      />
      {rawDatas.length > 0 && (
        <EdgeLabelRenderer>
          <div
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
            }}
            className={classNames('nopan', css(styles.label))}
          >
            {rawDatas.map((rawData) => (
              <RawDataPopover key={rawData.id} rawData={rawData}>
                <BenchlingIcon
                  className={css(styles.icon)}
                  icon={getIconForStepDataKind(getPipelineStepRawDataKind(rawData))}
                />
              </RawDataPopover>
            ))}
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
}

const styles = StyleSheet.create({
  icon: {
    color: Colors.GRAY_DARKER,
    ':hover': {
      color: Colors.BLUE,
    },
    height: ANALYSIS_FLOWCHART_EDGE_LABEL_ICON_HEIGHT,
    width: ANALYSIS_FLOWCHART_EDGE_LABEL_ICON_HEIGHT,
    marginLeft: 2,
    marginRight: 2,
  },
  label: {
    backgroundColor: ANALYSIS_FLOWCHART_BACKGROUND,
    padding: 2,
    // Without `cursor: pointer` and `pointerEvents: all`, the cursor will be 'grab' and will not active the
    // hover content
    cursor: 'pointer',
    pointerEvents: 'all',
  },
});
